import { BaseService } from './base-service';
import { eventBus } from '../utils/event-bus';
import { Events } from '@APP/events/app-events';
import { WebSectionTrackingData } from '@FLAVOR/../../common/types';
import { Question } from '@APP/model/question';
import axios, { AxiosInstance } from 'axios';
declare global {
    interface Window {
      dataLayer: Array<any>;
      LogRocket: any;
      uetq: Array<any>;
      hj?: Function;
      _tfa?: { push: (...args: any[]) => void };
    }
}

// save emarketmed_id on the zoho app
// [
//   { key: 'SRC', value: 'yacht-2' },
//   { key: 'emarketmed_id', value: 'XXXXXX' },
//   { key: 'utm_source', value: 'performance_marketing_other' },
//   { key: 'utm_medium', value: 'cpc' },
//   { key: 'utm_campaign', value: 'taboola_poc' }
// ]

const gtmAxiosInstance: AxiosInstance = axios.create({
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const enum gtmEvents {
    SIGN_IN = 'signin',
    CHAT_OPENED = 'chatOpened',
    SECTION_CHANGED = 'sectionChanged',
    //Not mapped to Google analytic events, will need to be defined as tags if relevant
    OTHER_ACTION = 'otherAction',
    PURCHASE = 'purchase',
    PACKAGES = 'packages',
    BILLING = 'billing',
}

type TaboolaEvent = Extract<gtmEvents, gtmEvents.SIGN_IN | gtmEvents.PURCHASE | gtmEvents.SECTION_CHANGED>;

type gtmData = {
  event?: string;
  userId?: string;
  email?: string;
  section?: string;
  next_section?: string;
  operation?: string;
  errorMessage?: string;
  value?: string;
  revenue?: string;
  emarketmed_id?: string;
  taboolaAlreadyReportedOnCheckout?: boolean;
};

type eventData = {
    event: string;
    userID: string;
    timeStamp: number;
    hutk: string | null;
    email?: string;
    section?: string;
    next_section?: string;
    operation?: string;
    errorMessage?: string;
    value?: string,
};

type Deps = { };

export class GtmService extends BaseService<Deps> {
  static serviceName: string = 'gtmService';

  private userID = '';
  private email = '';
  private demoMode = false;
  private emarketmed_id = '';

  constructor() {
    super();
    /** Questionnaire id set */
    eventBus().on<any>(Events.Type.QuestionnaireIdSet, ({ questionnaireId, questions }) => {
      this.userID = questionnaireId;
      const watercraftName = questions?.find((q:Question) => q.questionId === 'watercraft_name')?.value?.toLowerCase() || '';
      this.setDemoMode({ watercraftName });
    });
      
    /** Question update */
    eventBus().on(Events.Type.QuestionUpdate, (event:Question) => {
      if (event.questionId == 'watercraft_name') {
        const watercraft_name = event.value || '';
        const watercraftName = typeof watercraft_name === 'string'? watercraft_name?.toLowerCase() : '';
        this.setDemoMode({ watercraftName });
      }
    });

    /** Login */
    eventBus().on(Events.Type.LoginSuccess, async(data) => { await this.event(gtmEvents.SIGN_IN, data); });
       
    /** Purchase */
    eventBus().on(Events.Type.Congratulations, async(data) => {
      const { paidNowSuccessfully } = data;
      if (paidNowSuccessfully) {
        const { gtmPayload } = data;
        await this.event(gtmEvents.PURCHASE, gtmPayload);
      }
    });
      
    /** Chat opened */
    eventBus().on<any>(Events.Type.CloseAllTooltips, async(e: any) => {
      if (!e?.path) return;
      if (e.path.some((el: any) => el.dataset?.id === 'zsalesiq')) {
        await this.event(gtmEvents.CHAT_OPENED);
      }
      else return;
    });

    /** Section change */
    eventBus().on<WebSectionTrackingData>(Events.Type.SaveWebSectionTracking, async(data) => {
      const op = data?.operation;
      if (op === 'next') {
        await this.event(gtmEvents.SECTION_CHANGED, data);
      }
      else if (['load', 'welcome-next', 'send-otp'].includes(op)) {
        const d = Object.assign({}, data);
        d.section = op;
        await this.event(gtmEvents.SECTION_CHANGED, d);
      }
      else if (op && op !== 'prev') { await this.event(gtmEvents.OTHER_ACTION, data); }

      if (window.hj) {
        if (op === 'next' || op === 'prev') {
          window.hj('stateChange', `/marina/${data.next_section}`);
        } else if (op === 'load') {
          window.hj('stateChange', `/marina/${data.section}`);
        }
      }
    });  
  }
  
  public setEmarketmedId(emarketmed_id: string) {
    this.emarketmed_id = emarketmed_id;
  }
  
  private sectionNameToEventMap: Record<string, string> = {
    's_pricing': 'packages',
    's_billing': 'billing',
  };

  private nameToEventMap: Record<string, string> = {
    's_pricing': 'packages',
    's_billing': 'billing',
    'purchase': 'purchase',
    'signin': 'login',
  };
  
  // example call: https://api.emarketmed.com/postback?click_id=XXXXXX&event=login&payout=1000
  private async postToEmarketmed(clickId: string, event: string, revenue: string) {
    try {
      const url = `https://api.emarketmed.com/postback?click_id=${clickId}&event=${event}&payout=${revenue}`;
      await gtmAxiosInstance.get(url);
    } catch (err) {
      console.error('Failed to send postback to Emarketmed', err);
    }
  }
  
  private async trackTaboola(eventName: TaboolaEvent, data: gtmData) {
    try {
      // validate
      if (!window._tfa || !eventName) return;
      if (![gtmEvents.SIGN_IN, gtmEvents.PURCHASE, gtmEvents.SECTION_CHANGED].includes(eventName)) {
        return;
      }

      // prepare payload for taboola
      const revenue = data?.revenue || '0';
      let eventNameToTrack = '';
      if (eventName === gtmEvents.SECTION_CHANGED) {
        const section = data?.section?.toLowerCase() || '';
        eventNameToTrack = this.sectionNameToEventMap[section];
      } else {
        eventNameToTrack = this.nameToEventMap[eventName];
      }
      
      // report
      if (!data.taboolaAlreadyReportedOnCheckout) window._tfa.push({ notify: 'event', name: eventNameToTrack, id: 1879361, revenue });
      if (data?.emarketmed_id) {
        await this.postToEmarketmed(data?.emarketmed_id, eventNameToTrack, revenue);
      }
    } catch  {
      return;
    }
  }

  private getUserId(): string {
    return this.userID;
  }

  public getCookie(name: string = 'hubspotutk') {
    const cookieArr = document.cookie.split(';');
    for (let i = 0; i < cookieArr.length; i++) {
      const cookiePair = cookieArr[i].split('=');
      if (name == cookiePair[0].trim()) {
        return decodeURIComponent(cookiePair[1]);
      }
    }
    return null;
  }
  
  public getTimestamp() {
    const currentDate = new Date();
    const timestamp = currentDate.getTime();
    return timestamp;
  }

  private async event(eventName: gtmEvents, additionalData: gtmData = {}) {
    // exit on test applications
    if (this.demoMode) return;

    let eventData: eventData = {
      'event': eventName,
      'userID': this.getUserId(),
      'timeStamp': this.getTimestamp(),
      'hutk': this.getCookie('hubspotutk'),
      'email': this.email ?? '',
      ...additionalData,
    };

    window.dataLayer.push(eventData);
    if (eventName === 'signin') {
      this.email = eventData.email as string;
      if (window.LogRocket) {
        window.LogRocket.identify(this.getUserId(), {
          'email': this.email,
        });
      }
      window.uetq.push('event', 'login', {});
    }

    if (window.LogRocket) {
      window.LogRocket.track(eventName, eventData);
    }

    if ((window as any).pendo) {
      (window as any).pendo.initialize({
        visitor: {
            id: this.getUserId(), 
        }
      });

      (window as any).pendo.identify({
        visitor: {
            id: this.getUserId(), 
            email: this.email,
        },
      });
    }

    if (eventData?.section) {
      window.uetq.push('event', 'page_view', { page_path: eventData.section });
    }
    
    if (['signin', 'purchase', 'sectionChanged'].includes(eventName)) {
      if (eventName === 'sectionChanged') {
        const section = additionalData?.section?.toLowerCase() || '';
        if (!this.sectionNameToEventMap[section]) {
          return;
        } 
      }
      // get the emarketmed_id from the model and add it to the data
      const emarketmed_id = this.emarketmed_id;;
      if (emarketmed_id && typeof emarketmed_id === 'string') {
        additionalData.emarketmed_id = emarketmed_id;
      }
      await this.trackTaboola(eventName as TaboolaEvent, additionalData);
    }
  }

  private setDemoMode({ watercraftName }:{watercraftName:string}) {
    this.demoMode = (watercraftName.startsWith('ahoy-demo') || watercraftName.startsWith('ahoy-billing') || (watercraftName.startsWith('ahoy-testing') && !watercraftName.startsWith('ahoy-testing-event')));}  
  
}

GtmService.define();